import React, { useEffect, useState } from "react";
import { Box, Tabs, Tab, TextField, Typography, MenuItem, Button, Divider, Card, CardContent, IconButton, CircularProgress } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { ArrowBack } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { ConfirmLanguagesDialog } from "./confirm_languages_dialog";
import { addLandingPage, getSignedUrl } from "../../../services/landing_page/landing_page";
import { queryClient } from "../../../providers/query/query";
import { moduleTypes } from "../constants/landing-page-constants";
import MainLayout from "../../../layouts/main/main_layout";
import HTMLEditor from "../../../components/html_editor/html_editor";
import locales from "../../../constants/locales";
import SnackbarComp from "./snackbar";
import { MediaUpload } from "./media-upload";
import { RulesCreationForm } from "./rules-creation-form";

type LanguageFormData = {
    title: string;
    description: string;
};

type SharedFormData = {
    mediaType?: 'image' | 'tts' | 'audio' | 'video' | null;
    mediaFile?: FileList | string;
    userFile?: FileList;
    module?: string;
    module_id?: string;
    content_name?: string;
};

export const AddLandingPage: React.FC<{ viewMode: boolean }> = ({ viewMode = false }) => {
    const location = useLocation();
    const landingPageData = location.state?.landingPageData || null;

    const exceptedModules = ['HOSPITAL', 'NEEDS', 'PROFILE','REFERRAL','HOMEPAGE'];

    const navigate = useNavigate();
    const [mainTabIndex, setMainTabIndex] = useState(0); // 0: Landing Page, 1: Rules Creation (only in view mode)
    const [tabIndex, setTabIndex] = useState(0);
    const [isEditingRules, setIsEditingRules] = useState(false);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [rulesData, setRulesData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadingMedia, setIsUploadingMedia] = useState(false);
    const [snackbar, setSnackbar] = useState<any>({
        open: false,
        message: "",
        severity: "success",
    });

    // For TTS: keep pending TTS audio Files per language (to upload on final submit)
    const [ttsFilesByLanguage, setTtsFilesByLanguage] = useState<Record<string, File>>({});
    // For Audio: keep pending audio Files per language (to upload on final submit)
    const [audioFilesByLanguage, setAudioFilesByLanguage] = useState<Record<string, File>>({});
    // For Video: keep pending video Files per language (to upload on final submit)
    const [videoFilesByLanguage, setVideoFilesByLanguage] = useState<Record<string, File>>({});
    // For Image: keep pending image Files per language (to upload on final submit) - KEY CHANGE
    const [imageFilesByLanguage, setImageFilesByLanguage] = useState<Record<string, File>>({});
    // Separate image file for audio/TTS landing pages (notification-style) - per language
    const [separateImageFileByLanguage, setSeparateImageFileByLanguage] = useState<Record<string, File | string | undefined>>({});
    const [ObjectId, setObjectId] = useState<number | null>(null);

    const [sharedData, setSharedData] = useState<SharedFormData>({
        mediaType: null,
        mediaFile: undefined,
        userFile: undefined,
        module: "",
        module_id: "",
        content_name: "",
    });

    const [formData, setFormData] = useState<Record<string, LanguageFormData>>(
        locales.reduce((acc, lang) => {
            acc[lang.code] = { title: "", description: "" };
            return acc;
        }, {} as Record<string, LanguageFormData>)
    );

    const { control, reset, watch } = useForm<LanguageFormData>({
        defaultValues: { title: "", description: "" }
    });

    const { control: sharedControl, reset: resetSharedForm } = useForm<any>({
        defaultValues: {
            module: "",
            module_id: "",
            mediaType: null,
            mediaFile: '',
            userFile: '',
            content_name: ''
        },
    });
    const currentLang = locales[tabIndex]['code'];

    const watchedTitle = watch("title");
    const watchedDescription = watch("description");

    // Handle media upload error
    const handleMediaUploadError = (message: string) => {
        setSnackbar({
            open: true,
            message: message,
            severity: "error",
        });
    };

    // Handle media type change
    const handleMediaTypeChange = (type: 'image' | 'tts' | 'audio' | 'video' | null | undefined) => {
        setSharedData((prev: any) => ({ 
            ...prev, 
            mediaType: type,
            mediaFile: undefined,
        }));
        // Clear all language-specific files when switching away from their types
        if (type !== 'audio') {
            setAudioFilesByLanguage({});
        }
        if (type !== 'video') {
            setVideoFilesByLanguage({});
        }
        if (type !== 'tts') {
            setTtsFilesByLanguage({});
        }
        if (type !== 'image') {
            setImageFilesByLanguage({});
        }
        // Clear secondary image unless staying on audio/tts
        if (type !== 'audio' && type !== 'tts') {
            setSeparateImageFileByLanguage({});
        }
    };

    // Handle media file change
    const handleMediaFileChange = (file: FileList | null) => {
        setSharedData((prev: any) => ({ 
            ...prev, 
            mediaFile: file || undefined,
        }));
    };

    // Handle image file change per language - KEY CHANGE
    const handleImageFileChange = (language: string, file: File) => {
        setImageFilesByLanguage(prev => ({
            ...prev,
            [language]: file,
        }));
    };

    // Handle separate image file change (for audio/TTS)
    const handleSeparateImageFileChange = (language: string, file: File | null) => {
        if (file === null) {
            setSeparateImageFileByLanguage(prev => {
                const updated = { ...prev };
                delete updated[language];
                return updated;
            });
        } else {
            setSeparateImageFileByLanguage(prev => ({
                ...prev,
                [language]: file,
            }));
        }
    };

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        const newLang = locales[newValue].code;
        setTabIndex(newValue);
        reset(formData[newLang]);

        // When viewing an existing landing page, switch media to match the selected language
        if (viewMode && landingPageData) {
            const contents = landingPageData.contents || {};
            const contentForLang = contents[`content_${newLang}`] || {};

            // Prefer explicit per-language media types first; image can be an optional companion for audio/tts.
            if (contentForLang.tts) {
                setSharedData(prev => ({
                    ...prev,
                    mediaType: 'tts',
                    mediaFile: contentForLang.tts,
                }));
                if (contentForLang.image) {
                    setSeparateImageFileByLanguage(prev => ({ ...prev, [newLang]: contentForLang.image }));
                }
            } else if (contentForLang.audio) {
                setSharedData(prev => ({
                    ...prev,
                    mediaType: 'audio',
                    mediaFile: contentForLang.audio,
                }));
                if (contentForLang.image) {
                    setSeparateImageFileByLanguage(prev => ({ ...prev, [newLang]: contentForLang.image }));
                }
            } else if (contentForLang.video) {
                setSharedData(prev => ({
                    ...prev,
                    mediaType: 'video',
                    mediaFile: contentForLang.video,
                }));
                setSeparateImageFileByLanguage(prev => {
                    const updated = { ...prev };
                    delete updated[newLang];
                    return updated;
                });
            } else if (contentForLang.media_url) {
                // Check if media_url is audio or video
                const mediaUrl = contentForLang.media_url;
                if (mediaUrl.includes('audio_') || mediaUrl.match(/\.(mp3|wav|ogg)$/i)) {
                    setSharedData(prev => ({
                        ...prev,
                        mediaType: 'audio',
                        mediaFile: mediaUrl,
                    }));
                    if (contentForLang.image) {
                        setSeparateImageFileByLanguage(prev => ({ ...prev, [newLang]: contentForLang.image }));
                    }
                } else if (mediaUrl.includes('video_') || mediaUrl.match(/\.(mp4|webm|ogg)$/i)) {
                    setSharedData(prev => ({
                        ...prev,
                        mediaType: 'video',
                        mediaFile: mediaUrl,
                    }));
                    setSeparateImageFileByLanguage(prev => {
                        const updated = { ...prev };
                        delete updated[newLang];
                        return updated;
                    });
                }
            } else if (contentForLang.image) {
                // Image-only landing page
                setSharedData(prev => ({
                    ...prev,
                    mediaType: 'image',
                    mediaFile: contentForLang.image,
                }));
                setSeparateImageFileByLanguage(prev => {
                    const updated = { ...prev };
                    delete updated[newLang];
                    return updated;
                });
            }
        }
    };

    const onSubmitAll = () => {
        setIsLoading(true);

        const isEmptyRichText = (html: string = ""): boolean => {
            const plainText = html.replace(/<[^>]+>/g, "").trim();
            return plainText === "";
        };

        if (!formData['en']?.title) {
            setIsLoading(false);
            setSnackbar({
                open: true,
                message: `Title cannot be empty for english`,
                severity: "error",
            });
            return;
        }
        if (isEmptyRichText(formData['en']?.description)) {
            setIsLoading(false);
            setSnackbar({
                open: true,
                message: `Description cannot be empty for english`,
                severity: "error",
            });
            return;
        }

        for (const lang of locales) {
            const title = formData[lang.code]?.title?.trim();
            const description = formData[lang.code]?.description;

            const hasTitle = !!title;
            const hasDescription = !isEmptyRichText(description);

            if (!hasTitle && !hasDescription) continue;

            if (!hasTitle) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Title cannot be empty for ${lang.name}`,
                    severity: "error",
                });
                return;
            } else if (title.length < 10) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Title is too short for ${lang.name}`,
                    severity: "error",
                });
                return;
            } else if (title.length > 60) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Title is too long for ${lang.name}`,
                    severity: "error",
                });
                return;
            }

            if (!hasDescription) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Description cannot be empty for ${lang.name}`,
                    severity: "error",
                });
                return;
            } else if (description.length < 10) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Description is too short for ${lang.name}`,
                    severity: "error",
                });
                return;
            } else if (description.length > 1500) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Description is too long for ${lang.name}`,
                    severity: "error",
                });
                return;
            }
        }

        if (sharedData?.module && !sharedData?.module_id) {
            if (sharedData?.module && !exceptedModules.includes(sharedData?.module)) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Please add deeplink ${sharedData?.module === 'EXTERNAL' ? ' URL' : ' ID'}`,
                    severity: "error",
                });
                return;
            }
        }

        // Check if image file is selected when mediaType is 'image' - per language
        if (sharedData?.mediaType === 'image') {
            // Check if at least one language has an image
            const hasAnyImage = Object.keys(imageFilesByLanguage).length > 0;
            if (!hasAnyImage) {
                setIsLoading(false);
                setSnackbar({
                    open: true,
                    message: `Please select an image file for at least one language. Image is mandatory for image type contents.`,
                    severity: "error",
                });
                return;
            }
        }

        setIsLoading(false);
        setConfirmDialogOpen(true);
    };

    const handleFinalSubmit = async () => {
        try {
            setIsUploadingMedia(false);
            setIsLoading(false);
            setIsUploadingMedia(true);
            
            let currentCampaignId: number | null = ObjectId;
            if (!currentCampaignId) {
                currentCampaignId = Number(parseInt(
                    new Date().valueOf().toString() +
                    Math.floor(Math.random() * 100)
                        .toString()
                        .padEnd(2, "0"),
                ).toString());
                setObjectId(currentCampaignId);
            }

            const getContentType = (file: File): string => {
                if (file.type) return file.type;
                const extension = file.name.split('.').pop()?.toLowerCase();
                if (extension === 'jpg' || extension === 'jpeg') return 'image/jpeg';
                if (extension === 'png') return 'image/png';
                return 'image/jpeg';
            };

            // Upload images per language if mediaType is 'image' - KEY CHANGE
            let imageUploadedFileNamesByLang: Record<string, string> = {};
            if (sharedData?.mediaType === 'image') {
                if (!Object.keys(imageFilesByLanguage).length) {
                    setSnackbar({
                        open: true,
                        message: "Please upload an image file for at least one language before submitting.",
                        severity: "error",
                    });
                    return;
                }

                for (const [langCode, file] of Object.entries(imageFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
                    const safeLang = langCode.toLowerCase();
                    const fileName = `image_${safeLang}.${ext}`;
                    const contentType = getContentType(file);

                    const signedUrlResponse: any = await getSignedUrl({
                        fileName,
                        bucketType: "landingPage",
                        contentType: contentType,
                        ObjectId: currentCampaignId.toString(),
                    });

                    const signedUrl =
                        signedUrlResponse?.data?.data?.signed_url ||
                        signedUrlResponse?.data?.data?.url ||
                        signedUrlResponse?.data?.signedUrl ||
                        signedUrlResponse?.data?.url;

                    if (!signedUrl) {
                        throw new Error("Failed to get signed URL for image upload");
                    }

                    const fileBlob = await file.arrayBuffer();
                    const uploadResponse = await fetch(signedUrl, {
                        method: "PUT",
                        body: fileBlob,
                        headers: {
                            "Content-Type": contentType,
                        },
                    });

                    if (!uploadResponse.ok) {
                        throw new Error("Failed to upload image file");
                    }

                    imageUploadedFileNamesByLang[langCode] = fileName;
                }
            }

            // Upload separate image files for Audio/TTS per language (notification-style)
            const uploadedSecondaryImageFileNamesByLang: Record<string, string> = {};
            if (sharedData?.mediaType === 'audio' || sharedData?.mediaType === 'tts') {
                for (const [langCode, separateImageFile] of Object.entries(separateImageFileByLanguage)) {
                    if (!separateImageFile) continue;
                    
                    if (typeof separateImageFile === 'string') {
                        const urlParts = separateImageFile.split('/');
                        uploadedSecondaryImageFileNamesByLang[langCode] = urlParts[urlParts.length - 1].split('?')[0];
                    } else if (separateImageFile instanceof File) {
                        const imageFile = separateImageFile;
                        const imageExt = imageFile.name.split(".").pop()?.toLowerCase() || "jpg";
                        const imageFileName = `landing_page_image_${langCode.toLowerCase()}.${imageExt}`;
                        const contentType = getContentType(imageFile);

                        const signedUrlResponse: any = await getSignedUrl({
                            fileName: imageFileName,
                            bucketType: "landingPage",
                            contentType: contentType,
                            ObjectId: currentCampaignId.toString(),
                        });

                        const signedUrl =
                            signedUrlResponse?.data?.data?.signed_url ||
                            signedUrlResponse?.data?.data?.url ||
                            signedUrlResponse?.data?.signedUrl ||
                            signedUrlResponse?.data?.url;

                        if (!signedUrl) {
                            throw new Error("Failed to get signed URL for image upload");
                        }

                        const fileBlob = await imageFile.arrayBuffer();
                        const uploadResponse = await fetch(signedUrl, {
                            method: "PUT",
                            body: fileBlob,
                            headers: {
                                "Content-Type": contentType,
                            },
                        });

                        if (!uploadResponse.ok) {
                            throw new Error("Failed to upload image file");
                        }

                        uploadedSecondaryImageFileNamesByLang[langCode] = imageFileName;
                    }
                }
            }

            // Upload TTS files per language
            let ttsUploadedFileNamesByLang: Record<string, string> = {};
            if (sharedData?.mediaType === 'tts') {
                if (!Object.keys(ttsFilesByLanguage).length) {
                    setSnackbar({
                        open: true,
                        message: "Please convert text to speech for at least one language before submitting.",
                        severity: "error",
                    });
                    return;
                }

                for (const [langCode, file] of Object.entries(ttsFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "mp3").toLowerCase();
                    const safeLang = langCode.toLowerCase();
                    const fileName = `tts_audio_${safeLang}.${ext}`;

                    const signedUrlResponse: any = await getSignedUrl({
                        fileName,
                        bucketType: "landingPage",
                        contentType: file.type || "audio/mpeg",
                        ObjectId: currentCampaignId.toString(),
                    });

                    const signedUrl =
                        signedUrlResponse?.data?.data?.signed_url ||
                        signedUrlResponse?.data?.data?.url ||
                        signedUrlResponse?.data?.signedUrl ||
                        signedUrlResponse?.data?.url;

                    if (!signedUrl) {
                        throw new Error("Failed to get signed URL for TTS upload");
                    }

                    const fileBlob = await file.arrayBuffer();
                    const uploadResponse = await fetch(signedUrl, {
                        method: "PUT",
                        body: fileBlob,
                        headers: {
                            "Content-Type": file.type || "audio/mpeg",
                        },
                    });

                    if (!uploadResponse.ok) {
                        throw new Error("Failed to upload TTS audio file");
                    }

                    ttsUploadedFileNamesByLang[langCode] = fileName;
                }
            }

            // Upload audio files per language
            let audioUploadedFileNamesByLang: Record<string, string> = {};
            if (sharedData?.mediaType === 'audio') {
                if (!Object.keys(audioFilesByLanguage).length) {
                    setSnackbar({
                        open: true,
                        message: "Please upload an audio file for at least one language before submitting.",
                        severity: "error",
                    });
                    return;
                }

                for (const [langCode, file] of Object.entries(audioFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "mp3").toLowerCase();
                    const safeLang = langCode.toLowerCase();
                    const fileName = `audio_${safeLang}.${ext}`;

                    const signedUrlResponse: any = await getSignedUrl({
                        fileName,
                        bucketType: "landingPage",
                        contentType: file.type || "audio/mpeg",
                        ObjectId: currentCampaignId.toString(),
                    });

                    const signedUrl =
                        signedUrlResponse?.data?.data?.signed_url ||
                        signedUrlResponse?.data?.data?.url ||
                        signedUrlResponse?.data?.signedUrl ||
                        signedUrlResponse?.data?.url;

                    if (!signedUrl) {
                        throw new Error("Failed to get signed URL for audio upload");
                    }

                    const fileBlob = await file.arrayBuffer();
                    const uploadResponse = await fetch(signedUrl, {
                        method: "PUT",
                        body: fileBlob,
                        headers: {
                            "Content-Type": file.type || "audio/mpeg",
                        },
                    });

                    if (!uploadResponse.ok) {
                        throw new Error("Failed to upload audio file");
                    }

                    audioUploadedFileNamesByLang[langCode] = fileName;
                }
            }

            // Upload video files per language
            let videoUploadedFileNamesByLang: Record<string, string> = {};
            if (sharedData?.mediaType === 'video') {
                if (!Object.keys(videoFilesByLanguage).length) {
                    setSnackbar({
                        open: true,
                        message: "Please upload a video file for at least one language before submitting.",
                        severity: "error",
                    });
                    return;
                }

                for (const [langCode, file] of Object.entries(videoFilesByLanguage)) {
                    const ext = (file.name.split(".").pop() || "mp4").toLowerCase();
                    const safeLang = langCode.toLowerCase();
                    const fileName = `video_${safeLang}.${ext}`;

                    const signedUrlResponse: any = await getSignedUrl({
                        fileName,
                        bucketType: "landingPage",
                        contentType: file.type || "video/mp4",
                        ObjectId: currentCampaignId.toString(),
                    });

                    const signedUrl =
                        signedUrlResponse?.data?.data?.signed_url ||
                        signedUrlResponse?.data?.data?.url ||
                        signedUrlResponse?.data?.signedUrl ||
                        signedUrlResponse?.data?.url;

                    if (!signedUrl) {
                        throw new Error("Failed to get signed URL for video upload");
                    }

                    const fileBlob = await file.arrayBuffer();
                    const uploadResponse = await fetch(signedUrl, {
                        method: "PUT",
                        body: fileBlob,
                        headers: {
                            "Content-Type": file.type || "video/mp4",
                        },
                    });

                    if (!uploadResponse.ok) {
                        throw new Error("Failed to upload video file");
                    }

                    videoUploadedFileNamesByLang[langCode] = fileName;
                }
            }

            let mediaTypeToSend = null;

            // Build content payload with per-language media - KEY CHANGE: images are per language
            const contentPayload = locales.reduce((acc: any, lang: any) => {
                const langCode = lang.code;
                const { title, description } = formData[langCode] || {};
                const hasContent = title?.trim() || description?.trim();

                if (hasContent) {
                    const contentForLang: any = { title, description };

                    // For Image, attach per-language image file (if uploaded) into contents
                    if (sharedData?.mediaType === 'image') {
                        const imageFileNameForLang = imageUploadedFileNamesByLang[langCode];
                        if (imageFileNameForLang) {
                            contentForLang.image = imageFileNameForLang;
                            mediaTypeToSend = 'image';
                        }
                    }

                    // For TTS, attach per-language audio file (if uploaded) into contents
                    if (sharedData?.mediaType === 'tts') {
                        const audioFileNameForLang = ttsUploadedFileNamesByLang[langCode];
                        if (audioFileNameForLang) {
                            contentForLang.tts = audioFileNameForLang;
                        }
                        const secondaryImageForLang = uploadedSecondaryImageFileNamesByLang[langCode];
                        if (secondaryImageForLang) {
                            contentForLang.image = secondaryImageForLang;
                            mediaTypeToSend = 'image';
                        }
                    }
                    
                    // For Audio, attach per-language audio file (if uploaded) into contents
                    if (sharedData?.mediaType === 'audio') {
                        const audioFileNameForLang = audioUploadedFileNamesByLang[langCode];
                        if (audioFileNameForLang) {
                            contentForLang[`${sharedData?.mediaType}`] = audioFileNameForLang;
                        }
                        const secondaryImageForLang = uploadedSecondaryImageFileNamesByLang[langCode];
                        if (secondaryImageForLang) {
                            contentForLang.image = secondaryImageForLang;
                        }
                    }
                    
                    // For Video, attach per-language video file (if uploaded) into contents
                    if (sharedData?.mediaType === 'video') {
                        const videoFileNameForLang = videoUploadedFileNamesByLang[langCode];
                        if (videoFileNameForLang) {
                            contentForLang[`${sharedData?.mediaType}`] = videoFileNameForLang;
                        }
                    }
                    acc[`content_${langCode}`] = contentForLang;
                }
                return acc;
            }, {});

            const formDataPayload:any ={};
            
            // Send media_type
            // if (sharedData?.mediaType) {
            //     formDataPayload.append("media_type", sharedData.mediaType);
            // }

            // Send content_name at top level (outside contents)
            if (sharedData?.content_name) {
                formDataPayload.content_name = sharedData.content_name;
            }

            // Send rules data if available
            if (rulesData) {
                Object.assign(formDataPayload, rulesData);
            }

            // Always add content ID (as number)
            formDataPayload.content_id = currentCampaignId.toString();
            formDataPayload.media_type = sharedData?.mediaType;
            
            if (sharedData?.userFile?.[0]) {
                formDataPayload.userFile = sharedData.userFile[0];
            }
            if (sharedData?.module) {
                formDataPayload.module = sharedData.module;
            }
            if (sharedData?.module_id && sharedData?.module!=='HOSPITAL' && sharedData?.module!=='NEEDS') {
                formDataPayload.module_id = sharedData.module_id;
            }
            console.log("contentPayload",contentPayload);

            formDataPayload.contents = contentPayload;
            setIsUploadingMedia(false);
            setIsLoading(true);

            await addLandingPage(formDataPayload);
            setIsLoading(false);
            queryClient.invalidateQueries({ queryKey: ["landing-page-contents"] });

            setSnackbar({
                open: true,
                message: "Content added successfully!",
                severity: "success",
            });
            setConfirmDialogOpen(false);
            navigate('/landing-page')
        } catch (error: any) {
            setIsLoading(false);
            setIsUploadingMedia(false);
            console.error("Submission failed:", error);
            const errorMsg = error?.response?.data?.error?.message || "Something went wrong.";
            setSnackbar({
                open: true,
                message: `${errorMsg}`,
                severity: "error",
            });
        }
    };

    const filledLanguages = locales.filter(
        (lang) => formData[lang.code]?.title.trim() && formData[lang.code]?.description.trim()
    ).map((lang) => lang.name);

    useEffect(() => {
        if (viewMode) {
            reset(formData[currentLang]);
        }
    }, [tabIndex]);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            [currentLang]: {
                title: watchedTitle,
                description: watchedDescription
            }
        }));
    }, [watchedTitle, watchedDescription, currentLang]);

    useEffect(() => {
        if (viewMode && landingPageData) {
            const contents = landingPageData.contents || {};
            const newFormData: Record<string, LanguageFormData> = {};
            const newUploadedAudioByLanguage: Record<string, string> = {};
            const newUploadedVideoByLanguage: Record<string, string> = {};
            const newUploadedTtsByLanguage: Record<string, string> = {};
            const newUploadedImageByLanguage: Record<string, string> = {};

            // Handle both flat structure (from GET API) and contents structure
            if (landingPageData.contents && Object.keys(landingPageData.contents).length > 0) {
                // Has contents object (per-language structure)
                Object.entries(contents).forEach(([key, value]: any) => {
                    const langCode = key.replace("content_", "");
                    newFormData[langCode] = {
                        title: value?.title || "",
                        description: value?.description || ""
                    };

                    // Store per-language media URLs
                    if (value?.image) {
                        newUploadedImageByLanguage[langCode] = value.image;
                    }
                    if (value?.tts) {
                        newUploadedTtsByLanguage[langCode] = value.tts;
                    }
                    if (value?.audio) {
                        newUploadedAudioByLanguage[langCode] = value.audio;
                    }
                    if (value?.video) {
                        newUploadedVideoByLanguage[langCode] = value.video;
                    }
                    if (value?.media_url) {
                        const mediaUrl = value.media_url;
                        if (mediaUrl.includes('audio_') || mediaUrl.match(/\.(mp3|wav|ogg)$/i)) {
                            newUploadedAudioByLanguage[langCode] = mediaUrl;
                        } else if (mediaUrl.includes('video_') || mediaUrl.match(/\.(mp4|webm|ogg)$/i)) {
                            newUploadedVideoByLanguage[langCode] = mediaUrl;
                        }
                    }
                });
            } else {
                // Flat structure - set same data for all languages
                locales.forEach((lang) => {
                    newFormData[lang.code] = {
                        title: landingPageData.title || "",
                        description: landingPageData.description || ""
                    };
                });
            }

            // Derive media type from data or from first language with media
            let mediaType: 'image' | 'tts' | 'audio' | 'video' | undefined | null = 
                landingPageData.mediaType || landingPageData.media_type;
            
            if (!mediaType) {
                // Find first language with any media to determine type
                const firstLangWithMedia = locales.find((lang) => {
                    const key = `content_${lang.code}`;
                    const c = contents[key] || {};
                    return !!(c?.tts || c?.audio || c?.video || c?.media_url || c?.image);
                });
                if (firstLangWithMedia) {
                    const key = `content_${firstLangWithMedia.code}`;
                    const c = contents[key] || {};
                    if (c.tts) mediaType = 'tts';
                    else if (c.audio || (c.media_url && /audio_/i.test(c.media_url))) mediaType = 'audio';
                    else if (c.video || (c.media_url && /video_/i.test(c.media_url))) mediaType = 'video';
                    else if (c.image) mediaType = 'image';
                }
            }
            
            // Get media for current language
            let mediaFileUrl: string | undefined;
            const contentForCurrentLang = contents[`content_${currentLang}`] || {};
            
            if (contentForCurrentLang.tts) {
                mediaType = 'tts';
                mediaFileUrl = contentForCurrentLang.tts;
            } else if (contentForCurrentLang.audio) {
                mediaType = 'audio';
                mediaFileUrl = contentForCurrentLang.audio;
            } else if (contentForCurrentLang.video) {
                mediaType = 'video';
                mediaFileUrl = contentForCurrentLang.video;
            } else if (contentForCurrentLang.media_url) {
                const mediaUrl = contentForCurrentLang.media_url;
                if (mediaUrl.includes('audio_') || mediaUrl.match(/\.(mp3|wav|ogg)$/i)) {
                    mediaType = 'audio';
                    mediaFileUrl = mediaUrl;
                } else if (mediaUrl.includes('video_') || mediaUrl.match(/\.(mp4|webm|ogg)$/i)) {
                    mediaType = 'video';
                    mediaFileUrl = mediaUrl;
                }
            } else if (contentForCurrentLang.image) {
                mediaType = 'image';
                mediaFileUrl = contentForCurrentLang.image;
            }

            // Set secondary images for audio/TTS per language
            const secondaryImagesByLang: Record<string, string> = {};
            if (landingPageData.contents && Object.keys(landingPageData.contents).length > 0) {
                Object.entries(contents).forEach(([key, value]: any) => {
                    const langCode = key.replace("content_", "");
                    // If this language has audio/TTS and also has an image, set it as secondary image
                    if ((value?.audio || value?.tts || (value?.media_url && (value.media_url.includes('audio_') || value.media_url.match(/\.(mp3|wav|ogg)$/i)))) && value?.image) {
                        secondaryImagesByLang[langCode] = value.image;
                    }
                });
            }
            setSeparateImageFileByLanguage(secondaryImagesByLang);

            setFormData(newFormData);
            setSharedData({
                module: landingPageData.module,
                module_id: landingPageData.module_id,
                mediaType: mediaType,
                mediaFile: mediaFileUrl || undefined,
                userFile: landingPageData.user_file_url || undefined,
                content_name: landingPageData.content_name || "",
            });

            reset(newFormData[currentLang]);
            resetSharedForm({
                module: landingPageData.module,
                module_id: landingPageData.module_id,
                mediaType: mediaType || null,
                content_name: landingPageData.content_name || ""
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, landingPageData, currentLang]);

    return (
        <MainLayout>
            <Card sx={{ width: "100%", borderRadius: 2, m: 1 }} elevation={1}>
                <CardContent>
                    <Box display="flex" flexDirection="row">
                        <Box width={400} display="flex" alignItems="center">
                            <IconButton onClick={() => navigate(-1)}>
                                <ArrowBack />
                            </IconButton>
                            <Typography variant="h6" fontWeight="600" paddingLeft={1}>
                                {viewMode ? "View Content" : "Add Content"}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Show content based on tab index (view mode) or always show landing page (add mode) */}
                    {(!viewMode || mainTabIndex === 0) && (
                        <>
                            {/* Language Tabs */}
                            <Tabs value={tabIndex} onChange={handleTabChange}>
                                {locales.map((locale) => (
                                    <Tab key={locale.code} label={locale.name} />
                                ))}
                            </Tabs>

                            <Divider sx={{ mb: 2 }} />

                            {currentLang === "en" && (
                                <Controller
                                    name="content_name"
                                    control={sharedControl}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            label="Content Name"
                                            margin="normal"
                                            disabled={viewMode}
                                            onChange={e => {
                                                field.onChange(e);
                                                setSharedData(prev => ({ ...prev, content_name: e.target.value }));
                                            }}
                                        />
                                    )}
                                />
                            )}

                            <Controller
                                name="title"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        fullWidth
                                        label={currentLang === 'en' ? <>Title <span style={{ color: 'red' }}>*</span></> : 'Title'}
                                        margin="normal"
                                        disabled={viewMode}
                                    />
                                )}
                            />

                            <Controller
                                name="description"
                                control={control}
                                render={({ field }) => (
                                    <HTMLEditor
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={`Enter Description ${currentLang === 'en' ? '*' : ''}`}
                                        readOnly={viewMode}
                                    />
                                )}
                            />

                            {currentLang === "en" && (
                                <>
                                    {(!viewMode || (viewMode && sharedData?.module)) && <Controller
                                        name="module"
                                        control={sharedControl}
                                        render={({ field }) => (
                                            <TextField
                                                select
                                                label="Select Module Type"
                                                fullWidth
                                                margin="normal"
                                                {...field}
                                                disabled={viewMode}
                                                onChange={e => {
                                                    field.onChange(e);
                                                    setSharedData(prev => ({ ...prev, module: e.target.value }));
                                                }}
                                            >
                                                {moduleTypes.map(type => (
                                                    <MenuItem key={type} value={type}>
                                                        {type}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    />}

                                    {(!viewMode || (viewMode && sharedData?.module)) && (!exceptedModules.includes(sharedData?.module || '')) && <Controller
                                        name="module_id"
                                        control={sharedControl}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label={
                                                    sharedData.module === "EXTERNAL"
                                                        ? "Deeplink URL"
                                                        : sharedData.module === "JOB"
                                                            ? "Category ID"
                                                            : "Module ID"
                                                }
                                                margin="normal"
                                                disabled={viewMode}
                                                onChange={e => {
                                                    field.onChange(e);
                                                    setSharedData(prev => ({ ...prev, module_id: e.target.value }));
                                                }}
                                            />
                                        )}
                                    />}
                                </>
                            )}

                            <MediaUpload
                                tabIndex={tabIndex}
                                viewMode={viewMode}
                                mediaType={sharedData.mediaType || null}
                                setSharedData={setSharedData}
                                mediaFile={sharedData.mediaFile}
                                control={sharedControl}
                                title={formData[currentLang]?.title || ''}
                                language={currentLang}
                                onMediaTypeChange={handleMediaTypeChange}
                                onMediaFileChange={handleMediaFileChange}
                                onUploadSuccess={() => {}}
                                onError={handleMediaUploadError}
                                onTtsGenerated={(langCode, file) => {
                                    setTtsFilesByLanguage(prev => ({
                                        ...prev,
                                        [langCode]: file,
                                    }));
                                }}
                                onAudioFileChange={(langCode, file) => {
                                    setAudioFilesByLanguage(prev => ({
                                        ...prev,
                                        [langCode]: file,
                                    }));
                                }}
                                onVideoFileChange={(langCode, file) => {
                                    setVideoFilesByLanguage(prev => ({
                                        ...prev,
                                        [langCode]: file,
                                    }));
                                }}
                                onImageFileChange={handleImageFileChange}
                                imageFile={separateImageFileByLanguage[currentLang]}
                                onImageFileChangeSecondary={handleSeparateImageFileChange}
                            />

                            {!viewMode && currentLang === 'en' && (
                                <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center', gap: 2 }}>
                                    {isUploadingMedia && (
                                        <Typography variant="body2" color="text.secondary">
                                            Uploading media...
                                        </Typography>
                                    )}
                                    <Button
                                        disabled={isLoading || isUploadingMedia}
                                        onClick={onSubmitAll}
                                        variant="contained"
                                        color="primary"
                                        sx={{ mt: 2 }}
                                        startIcon={isUploadingMedia ? <CircularProgress size={16} color="inherit" /> : null}
                                    >
                                        {isUploadingMedia ? 'Uploading Media...' : 'Add Content'}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}

                    
                </CardContent>
            </Card>

            <ConfirmLanguagesDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleFinalSubmit}
                filledLanguages={filledLanguages}
                isLoading={isLoading || isUploadingMedia}
                isEdit={false}
            />

            <SnackbarComp
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            />
        </MainLayout>
    );
};
